import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';

import * as dailyInsightsActions from '../../store/monetizationOrders/dailyInsights/actions';
import * as monetizationOrdersActions from '../../store/monetizationOrders/actions';
import { useDispatch, useSelector } from 'react-redux';
import {
  totalRevenues,
  totalPositiveQualifiedCosts,
  totalSupplierCosts,
  totalCosts,
} from '../../selectors/monetizationOrder';
import { selectSupplierByType } from '../../selectors/supplier';

import Modal from '../../components/Common/Modal/Modal';
import ShowSingleElement from '../../components/Common/ShowSingleElement';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DateRangeFilter from '../../components/Common/DateRangeFilter/DateRangeFilter';
import MiniCard from '../../components/Common/MiniCard';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import createTableColumns from '../../components/MonetizationOrders/DailyInsights/tableColumns';
import {
  CountRowTablePlacement,
  ExportButtonPlacement,
  ToggleColumnButton,
} from '../../components/Common/DataTable/tableActionButtons';
import { addOrRemove } from '../../helpers/sharedFunction';
import moment from 'moment';
import supplierTypes from '../../config/supplierTypes';
import { useTranslation } from 'react-i18next';

const DailyInsights = (props) => {
  const dailyInsights = useSelector(
    (state) => state.DailyMonetizationOrderInsights.insights,
  );
  const monetizationOrders = useSelector(
    (state) => state.MonetizationOrder.monetizationOrders,
  );
  const sales = useSelector(selectSupplierByType(supplierTypes.SALE));
  const loading = useSelector(
    (state) => state.DailyMonetizationOrderInsights.loading,
  );

  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const revenues = useSelector(totalRevenues);
  const positiveQualifiedCosts = useSelector(totalPositiveQualifiedCosts);
  const saleCosts = useSelector(totalSupplierCosts);
  const costs = useSelector(totalCosts);

  const [selectedInsight, setSelectedInsight] = useState(null);
  const [viewModal, setViewModal] = useState(false);

  const startDate = useSelector(
    (state) => state.DailyMonetizationOrderInsights.startDate,
  );
  const endDate = useSelector(
    (state) => state.DailyMonetizationOrderInsights.endDate,
  );
  const dataFilters = useSelector(
    (state) => state.DailyMonetizationOrderInsights.filters,
  );
  const momentStartDate = startDate ? moment(startDate) : null;
  const momentEndDate = endDate ? moment(endDate) : null;

  const maxDate = moment.utc();
  const minDate = moment.utc('04/07/2020', 'DD/MM/YYYY');

  const [selectedFilters, setSelectedFilters] = useState({
    startDate: momentStartDate || moment.utc().startOf('month'),
    endDate: momentEndDate || moment.utc(),
    monetizationOrderIds: filters?.monetizationOrderIds || null,
  });

  const dispatch = useDispatch();

  const refresh = useCallback(
    ({ startDate, endDate, monetizationOrderIds }) => {
      const selectedStartDate = startDate.utc().startOf('day').toDate();
      const selectedEndDate = endDate.utc().endOf('day').toDate();
      dispatch(
        dailyInsightsActions.fetchDailyInsights(
          selectedStartDate,
          selectedEndDate,
          {
            monetizationOrderIds,
          },
        ),
      );
      setSelectedFilters({ startDate, endDate, monetizationOrderIds });
    },
  );

  useEffect(() => {
    dailyInsights.length === 0 && refresh(selectedFilters);
    dispatch(monetizationOrdersActions.syncMonetizationOrders());
  }, []);

  const openViewModal = (insight) => {
    setSelectedInsight(insight);
    setViewModal(true);
  };

  const { t } = useTranslation();

  const metrics = [
    {
      title: t('Revenues'),
      text: `${isFinite(revenues) ? revenues.toFixed(0) : 0} €`,
      color: 'dark',
      icon: 'fas fa-laugh-wink',
      isSensitiveData: true,
    },
    {
      title: t('Positive Qualified Costs'),
      text: `${
        isFinite(positiveQualifiedCosts) ? positiveQualifiedCosts.toFixed(0) : 0
      } €`,
      color: 'danger',
      icon: 'fas fa-sad-tear',
      isSensitiveData: true,
    },
    {
      title: t('Sale Costs'),
      text: `${isFinite(saleCosts) ? saleCosts.toFixed(0) : 0} €`,
      color: 'danger',
      icon: 'fas fa-sad-tear',
      isSensitiveData: true,
    },
    {
      title: t('Costs'),
      text: `${isFinite(costs) ? costs.toFixed(0) : 0} €`,
      color: 'danger',
      icon: 'fas fa-sad-tear',
      isSensitiveData: true,
    },
  ];

  const columns = useMemo(
    () => createTableColumns(monetizationOrders, sales, openViewModal),
    [monetizationOrders, sales],
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );
  const handleToggleColumn = (name) => {
    setHiddenColumns(addOrRemove(hiddenColumns, name));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Daily Monetization Order Insights"
            breadcrumbItem="List"
            onRefresh={() => refresh(selectedFilters)}
            loading={loading}
          />
          <DateRangeFilter
            onUpdate={refresh}
            minDate={minDate}
            maxDate={maxDate}
            startDate={selectedFilters.startDate}
            endDate={selectedFilters.endDate}
            loading={loading}
            showMonetizationOrderFilters
            initialFilters={selectedFilters}
          />
          <Row>
            {metrics.map((metric, index) => (
              <Col md="3" key={'metric_' + index}>
                <MiniCard {...metric} loading={loading} />
              </Col>
            ))}
          </Row>
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end m-auto">
                    <CountRowTablePlacement />
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                    <ExportButtonPlacement />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={dailyInsights}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    currentPage={currentPage}
                    onCurrentPageChange={setCurrentPage}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal
            size="lg"
            title="Monetization Order Insight"
            isOpen={viewModal}
            toggle={() => setViewModal(!viewModal)}
            showClose
            scrollable={false}
          >
            <ShowSingleElement
              element={selectedInsight}
              icon="bx bx-message-square-detail"
            />
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DailyInsights;
